import React, { useState, useRef, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Camera, Sparkles, Activity, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { PoseLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODEL_URL = "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task";
const VISION_WASM = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm";

// MediaPipe Pose landmark indices
const LM = { NOSE: 0, LEFT_SHOULDER: 11, RIGHT_SHOULDER: 12, LEFT_HIP: 23, RIGHT_HIP: 24, LEFT_FOOT: 31, RIGHT_FOOT: 32 };

// Body height ratio: nose-to-foot distance ≈ 92% of full body height
const NOSE_TO_FOOT_RATIO = 0.92;

const AIVerticalMeasure = ({ open, onOpenChange, onSaved }) => {
  const [step, setStep] = useState("setup"); // setup | preview | countdown | measuring | result | error
  const [userHeightFt, setUserHeightFt] = useState("");
  const [userHeightIn, setUserHeightIn] = useState("");
  const [countdown, setCountdown] = useState(3);
  const [measurePhase, setMeasurePhase] = useState(""); // "stand_still" | "jump_now"
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [modelLoading, setModelLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const landmarkerRef = useRef(null);
  const rafRef = useRef(null);
  const baselineRef = useRef({ samples: [] }); // collected during stand-still
  const peakRef = useRef({ hipY: Infinity }); // min hipY = highest jump (y increases downward)

  // Cleanup on close
  useEffect(() => {
    if (!open) {
      cleanup();
      setStep("setup");
      setResult(null);
      setErrorMsg("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cleanup = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const totalHeightInches = () => {
    const ft = parseFloat(userHeightFt) || 0;
    const inch = parseFloat(userHeightIn) || 0;
    return ft * 12 + inch;
  };

  const loadModel = async () => {
    if (landmarkerRef.current) return landmarkerRef.current;
    setModelLoading(true);
    try {
      const filesetResolver = await FilesetResolver.forVisionTasks(VISION_WASM);
      const landmarker = await PoseLandmarker.createFromOptions(filesetResolver, {
        baseOptions: { modelAssetPath: MODEL_URL, delegate: "GPU" },
        runningMode: "VIDEO",
        numPoses: 1,
      });
      landmarkerRef.current = landmarker;
      return landmarker;
    } catch (e) {
      console.error("MediaPipe load failed:", e);
      throw new Error("Could not load AI model. Check your internet connection.");
    } finally {
      setModelLoading(false);
    }
  };

  const startCamera = async () => {
    const h = totalHeightInches();
    if (h < 36 || h > 96) {
      toast.error("Please enter a valid height (3'0\" – 8'0\").");
      return;
    }

    // Defensive subscription gate — backend is source of truth
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/subscription/feature-access`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.data?.has_advanced_ai) {
        setErrorMsg("AI Vertical Measure requires a Pro or Elite subscription. Upgrade to unlock.");
        setStep("error");
        return;
      }
    } catch (e) {
      setErrorMsg("Could not verify subscription. Please try again.");
      setStep("error");
      return;
    }

    setErrorMsg("");
    try {
      await loadModel();
    } catch (e) {
      setErrorMsg(e.message);
      setStep("error");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setStep("preview");
    } catch (e) {
      console.error("Camera error:", e);
      setErrorMsg("Camera access denied or unavailable. Allow camera permission and try again.");
      setStep("error");
    }
  };

  const switchCamera = async () => {
    if (!streamRef.current) return;
    const currentFacing = streamRef.current.getVideoTracks()[0]?.getSettings()?.facingMode;
    const newFacing = currentFacing === "user" ? "environment" : "user";
    streamRef.current.getTracks().forEach((t) => t.stop());
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: newFacing, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (e) {
      toast.error("Could not switch camera.");
    }
  };

  const beginMeasurement = async () => {
    baselineRef.current = { samples: [] };
    peakRef.current = { hipY: Infinity };

    // Countdown 3-2-1
    setStep("countdown");
    for (let i = 3; i >= 1; i--) {
      setCountdown(i);
      await new Promise((r) => setTimeout(r, 900));
    }

    // Measuring: 1.5s stand still, then 3.5s jump window
    setStep("measuring");
    setMeasurePhase("stand_still");
    const startTime = performance.now();
    const STAND_STILL_MS = 1500;
    const JUMP_WINDOW_MS = 3500;
    const TOTAL_MS = STAND_STILL_MS + JUMP_WINDOW_MS;

    let jumpStartTimeFired = false;

    const tick = (now) => {
      const elapsed = now - startTime;
      const video = videoRef.current;
      const landmarker = landmarkerRef.current;
      if (!video || !landmarker || video.readyState < 2) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      try {
        const detection = landmarker.detectForVideo(video, now);
        if (detection?.landmarks?.length > 0) {
          const lms = detection.landmarks[0];
          const hipY = (lms[LM.LEFT_HIP].y + lms[LM.RIGHT_HIP].y) / 2; // normalized 0..1
          const noseY = lms[LM.NOSE].y;
          const footY = (lms[LM.LEFT_FOOT].y + lms[LM.RIGHT_FOOT].y) / 2;
          const visibleEnough =
            lms[LM.LEFT_HIP].visibility > 0.6 &&
            lms[LM.RIGHT_HIP].visibility > 0.6 &&
            lms[LM.NOSE].visibility > 0.6 &&
            lms[LM.LEFT_FOOT].visibility > 0.4 &&
            lms[LM.RIGHT_FOOT].visibility > 0.4;

          if (visibleEnough) {
            if (elapsed < STAND_STILL_MS) {
              baselineRef.current.samples.push({ hipY, noseY, footY });
            } else {
              // Jump tracking phase
              if (!jumpStartTimeFired) {
                jumpStartTimeFired = true;
                setMeasurePhase("jump_now");
              }
              if (hipY < peakRef.current.hipY) {
                peakRef.current.hipY = hipY;
              }
            }
          }
        }

        // Draw overlay for user feedback
        drawOverlay(detection);
      } catch (e) {
        console.error("Pose detect error:", e);
      }

      if (elapsed < TOTAL_MS) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        finishMeasurement();
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const drawOverlay = (detection) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!detection?.landmarks?.length) return;
    const lms = detection.landmarks[0];

    // Draw key joints
    ctx.fillStyle = "#f97316";
    ctx.strokeStyle = "#f97316";
    ctx.lineWidth = 3;
    [LM.NOSE, LM.LEFT_SHOULDER, LM.RIGHT_SHOULDER, LM.LEFT_HIP, LM.RIGHT_HIP, LM.LEFT_FOOT, LM.RIGHT_FOOT].forEach((i) => {
      const p = lms[i];
      if (p.visibility > 0.5) {
        ctx.beginPath();
        ctx.arc(p.x * canvas.width, p.y * canvas.height, 6, 0, 2 * Math.PI);
        ctx.fill();
      }
    });

    // Draw hip-line (center)
    const lh = lms[LM.LEFT_HIP];
    const rh = lms[LM.RIGHT_HIP];
    if (lh.visibility > 0.5 && rh.visibility > 0.5) {
      ctx.beginPath();
      ctx.moveTo(lh.x * canvas.width, lh.y * canvas.height);
      ctx.lineTo(rh.x * canvas.width, rh.y * canvas.height);
      ctx.stroke();
    }
  };

  const finishMeasurement = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;

    const samples = baselineRef.current.samples;
    if (samples.length < 5) {
      setErrorMsg("Couldn't see your body clearly. Make sure your full body is visible — head to toes — and try again.");
      setStep("error");
      return;
    }

    // Average baseline measurements (normalized 0..1 image coords)
    const avg = (key) => samples.reduce((s, x) => s + x[key], 0) / samples.length;
    const baselineHipY = avg("hipY");
    const baselineNoseY = avg("noseY");
    const baselineFootY = avg("footY");

    // Convert normalized to pixels
    const videoH = videoRef.current.videoHeight;
    const baselineBodyPx = (baselineFootY - baselineNoseY) * videoH; // nose-to-foot pixels
    const userBodyInches = totalHeightInches() * NOSE_TO_FOOT_RATIO; // expected nose-to-foot inches
    const pxPerInch = baselineBodyPx / userBodyInches;

    if (pxPerInch < 1 || !isFinite(pxPerInch)) {
      setErrorMsg("Couldn't calibrate scale. Make sure full body is in frame and try again.");
      setStep("error");
      return;
    }

    const jumpPx = (baselineHipY - peakRef.current.hipY) * videoH; // since y increases downward, positive = jump up
    const jumpInches = jumpPx / pxPerInch;

    if (jumpInches < 1 || jumpInches > 60) {
      setErrorMsg(`Measurement out of range (${jumpInches.toFixed(1)}"). Try again with full body visible from the side.`);
      setStep("error");
      return;
    }

    setResult({
      inches: Math.round(jumpInches * 10) / 10,
      pxPerInch: pxPerInch.toFixed(2),
      baselineHipY: baselineHipY.toFixed(3),
      peakHipY: peakRef.current.hipY.toFixed(3),
    });
    setStep("result");
  };

  const saveJump = async () => {
    if (!result) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/jumps`,
        { height_inches: result.inches, notes: "Measured with AI Camera" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Saved! Vertical: ${result.inches}"`);
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error("Could not save jump. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const tryAgain = () => {
    setResult(null);
    setErrorMsg("");
    setStep("preview");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-zinc-950 border border-zinc-800 text-white p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-2 border-b border-zinc-800">
          <DialogTitle className="text-xl font-heading font-black uppercase tracking-tight flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-orange-500" />
            AI Vertical Measure
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 pb-6 pt-4">
          <AnimatePresence mode="wait">
            {/* SETUP */}
            {step === "setup" && (
              <motion.div
                key="setup"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                <p className="text-sm text-zinc-400">
                  Enter your height, then prop your phone against a wall facing you from <span className="text-orange-500 font-semibold">5–10 ft away</span>. Stand sideways so your full body is visible head-to-toe.
                </p>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="ht-ft" className="text-xs uppercase tracking-wide text-zinc-400">
                      Height (feet)
                    </Label>
                    <Input
                      id="ht-ft"
                      type="number"
                      min={3}
                      max={8}
                      value={userHeightFt}
                      onChange={(e) => setUserHeightFt(e.target.value)}
                      placeholder="5"
                      className="bg-zinc-900 border-zinc-700 text-white"
                      data-testid="ai-vertical-height-ft-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ht-in" className="text-xs uppercase tracking-wide text-zinc-400">
                      Height (inches)
                    </Label>
                    <Input
                      id="ht-in"
                      type="number"
                      min={0}
                      max={11}
                      value={userHeightIn}
                      onChange={(e) => setUserHeightIn(e.target.value)}
                      placeholder="10"
                      className="bg-zinc-900 border-zinc-700 text-white"
                      data-testid="ai-vertical-height-in-input"
                    />
                  </div>
                </div>

                <div className="rounded-md bg-orange-500/10 border border-orange-500/30 p-3 text-xs text-orange-200 space-y-1">
                  <p className="font-bold flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> Tips for accuracy:</p>
                  <ul className="list-disc list-inside space-y-0.5 ml-1 text-orange-100/80">
                    <li>Stand sideways to the camera (profile view)</li>
                    <li>Full body in frame — head to feet</li>
                    <li>Good lighting, plain background works best</li>
                    <li>Wear fitted clothes (loose clothes confuse the AI)</li>
                  </ul>
                </div>

                <Button
                  onClick={startCamera}
                  disabled={modelLoading}
                  className="w-full bg-orange-600 hover:bg-orange-500 text-white font-black uppercase h-12"
                  data-testid="ai-vertical-start-camera-btn"
                >
                  {modelLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading AI Model...</>
                  ) : (
                    <><Camera className="w-4 h-4 mr-2" /> Open Camera</>
                  )}
                </Button>
              </motion.div>
            )}

            {/* PREVIEW + COUNTDOWN + MEASURING share the video */}
            {(step === "preview" || step === "countdown" || step === "measuring") && (
              <motion.div
                key="camera"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                <div className="relative bg-black aspect-[9/16] sm:aspect-video w-full overflow-hidden rounded-md">
                  <video
                    ref={videoRef}
                    playsInline
                    muted
                    autoPlay
                    className="w-full h-full object-cover"
                  />
                  <canvas
                    ref={canvasRef}
                    className="absolute inset-0 w-full h-full pointer-events-none"
                  />

                  {/* Countdown overlay */}
                  {step === "countdown" && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <motion.div
                        key={countdown}
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 1.5, opacity: 0 }}
                        className="text-8xl font-heading font-black text-orange-500"
                      >
                        {countdown}
                      </motion.div>
                    </div>
                  )}

                  {/* Measuring overlay */}
                  {step === "measuring" && (
                    <div className="absolute top-4 inset-x-4 flex justify-center pointer-events-none">
                      <motion.div
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        className={`px-4 py-2 rounded-full font-black uppercase text-sm tracking-wider ${
                          measurePhase === "stand_still"
                            ? "bg-blue-600 text-white"
                            : "bg-orange-600 text-white animate-pulse"
                        }`}
                      >
                        {measurePhase === "stand_still" ? "🧍 Stand Still..." : "🚀 JUMP NOW!"}
                      </motion.div>
                    </div>
                  )}
                </div>

                {step === "preview" && (
                  <div className="flex gap-2">
                    <Button
                      onClick={switchCamera}
                      variant="outline"
                      className="flex-1 bg-zinc-900 border-zinc-700 text-white hover:bg-zinc-800"
                    >
                      <Camera className="w-4 h-4 mr-2" /> Switch
                    </Button>
                    <Button
                      onClick={beginMeasurement}
                      className="flex-[2] bg-orange-600 hover:bg-orange-500 text-white font-black uppercase h-12"
                      data-testid="ai-vertical-begin-btn"
                    >
                      <Activity className="w-4 h-4 mr-2" /> Start Measurement
                    </Button>
                  </div>
                )}
                {step === "preview" && (
                  <p className="text-xs text-zinc-500 text-center">
                    Position yourself in frame, then tap Start. You'll have 1.5 seconds to stand still, then jump as high as you can.
                  </p>
                )}
              </motion.div>
            )}

            {/* RESULT */}
            {step === "result" && result && (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-5 text-center py-4"
              >
                <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
                <div>
                  <p className="text-sm text-zinc-500 uppercase tracking-widest mb-2">Your Vertical Jump</p>
                  <p className="text-7xl font-heading font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">
                    {result.inches}<span className="text-4xl">"</span>
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={tryAgain}
                    variant="outline"
                    className="flex-1 bg-zinc-900 border-zinc-700 text-white hover:bg-zinc-800 font-black uppercase"
                    data-testid="ai-vertical-retry-btn"
                  >
                    Try Again
                  </Button>
                  <Button
                    onClick={saveJump}
                    disabled={saving}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-black uppercase"
                    data-testid="ai-vertical-save-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Jump"}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* ERROR */}
            {step === "error" && (
              <motion.div
                key="error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-4 text-center py-4"
              >
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
                <p className="text-sm text-zinc-300">{errorMsg}</p>
                <Button
                  onClick={() => { setStep("setup"); setErrorMsg(""); cleanup(); }}
                  className="w-full bg-orange-600 hover:bg-orange-500 text-white font-black uppercase"
                >
                  Back to Setup
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIVerticalMeasure;
